import React from "react";
import { Select, Space, Tabs } from "antd";
import { useSearchParams } from "react-router-dom";
import TrashTable from "./TrashTable";
import PageHeader from "../../../components/PageHeader";
import type { TrashEntity } from "../../../services/trash/trash.types";

const DEFAULT_DAYS = 90;

const DAYS_OPTIONS: { value: number; label: string }[] = [
  { value: 30, label: "Últimos 30 días" },
  { value: 90, label: "90 días" },
  { value: 365, label: "1 año" },
  { value: 3650, label: "Todo" },
];

const TAB_DEFINITIONS: { key: TrashEntity; label: string }[] = [
  { key: "resource", label: "Recursos" },
  { key: "lesson", label: "Lecciones" },
  { key: "course", label: "Cursos" },
  { key: "project", label: "Proyectos" },
  { key: "program", label: "Programas" },
];

const isTrashEntity = (value: string | null): value is TrashEntity =>
  TAB_DEFINITIONS.some(({ key }) => key === value);

const parseDays = (value: string | null): number => {
  const parsed = parseInt(value ?? "", 10);
  return DAYS_OPTIONS.some(({ value: days }) => days === parsed)
    ? parsed
    : DEFAULT_DAYS;
};

const Trash: React.FC = () => {
  // The URL is the source of truth for tab, days and page (the page param is
  // owned by TrashTable), so any view is deep-linkable and survives refreshes.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab: TrashEntity = isTrashEntity(tabParam) ? tabParam : "resource";
  const days = parseDays(searchParams.get("days"));

  // Changing tab or days always resets the page (a page computed for another
  // tab/period combination is meaningless).
  const setFilterParam = (key: "tab" | "days", value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(key, value);
      next.delete("page");
      return next;
    });
  };

  const tabItems = TAB_DEFINITIONS.map(({ key, label }) => ({
    key,
    label,
    children: <TrashTable entity={key} days={days} />,
  }));

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <PageHeader title="Papelera" />
      <Select
        value={days}
        onChange={(value) => setFilterParam("days", String(value))}
        options={DAYS_OPTIONS}
        style={{ width: 200 }}
        aria-label="Período"
      />
      {/* destroyInactiveTabPane keeps a single mounted table so the shared
          `page` URL param only ever drives the active tab; switching back is
          instant anyway because RTK Query serves the cached page. */}
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setFilterParam("tab", key)}
        items={tabItems}
        destroyInactiveTabPane
      />
    </Space>
  );
};

export default Trash;
